<template>
  <div class="df-tool">
    <div class="df-input-section">
      <div class="df-input-label">
        <span class="df-dot" />
        {{ $t('tool.DiffViewer.subtitle') }}
      </div>
      <div class="df-panes">
        <div class="df-pane">
          <div class="df-pane-title">{{ $t('tool.DiffViewer.paneA') }}</div>
          <el-input v-model="textA" type="textarea" :rows="10" :placeholder="$t('tool.DiffViewer.phA')" class="df-textarea" @input="compare" />
        </div>
        <div class="df-pane">
          <div class="df-pane-title">{{ $t('tool.DiffViewer.paneB') }}</div>
          <el-input v-model="textB" type="textarea" :rows="10" :placeholder="$t('tool.DiffViewer.phB')" class="df-textarea" @input="compare" />
        </div>
      </div>
    </div>

    <!-- Stats -->
    <div class="df-stats" v-if="hasContent">
      <span class="df-stat df-stat-green">{{ $t('tool.DiffViewer.same', { n: stats.same }) }}</span>
      <span class="df-stat df-stat-red">{{ $t('tool.DiffViewer.removed', { n: stats.removed }) }}</span>
      <span class="df-stat df-stat-blue">{{ $t('tool.DiffViewer.added', { n: stats.added }) }}</span>
      <span class="df-stat df-stat-orange">{{ $t('tool.DiffViewer.modified', { n: stats.modified }) }}</span>
      <el-button size="mini" plain icon="el-icon-document-copy" @click="copyDiff" v-if="stats.diffCount">{{ $t('tool.DiffViewer.copyDiff') }}</el-button>
    </div>

    <!-- Diff output -->
    <div class="df-output" v-if="diffs.length">
      <div class="df-lines">
        <div
          v-for="(d, i) in diffs" :key="i"
          class="df-line" :class="'df-line-' + d.type"
        >
          <span class="df-ln df-ln-a">{{ d.lnA || '' }}</span>
          <span class="df-ln df-ln-b">{{ d.lnB || '' }}</span>
          <span class="df-marker">{{ markers[d.type] }}</span>
          <span class="df-text" v-if="d.type === 'same'">{{ d.text }}</span>
          <span class="df-text" v-else-if="d.type === 'removed'" v-html="highlightChars(d.text, d.pairText)"></span>
          <span class="df-text" v-else-if="d.type === 'added'" v-html="highlightChars(d.text, d.pairText)"></span>
          <span class="df-text" v-else v-html="highlightChange(d.oldText, d.newText)"></span>
        </div>
      </div>
    </div>

    <div class="df-empty" v-if="!hasContent">{{ $t('tool.DiffViewer.empty') }}</div>
  </div>
</template>

<script>
function lcs(a, b) {
  var m = a.length, n = b.length
  var dp = new Array(m + 1)
  for (var i = 0; i <= m; i++) { dp[i] = new Array(n + 1); dp[i][0] = 0 }
  for (var j = 0; j <= n; j++) dp[0][j] = 0
  for (var i = 1; i <= m; i++) {
    for (var j = 1; j <= n; j++) {
      if (a[i-1] === b[j-1]) dp[i][j] = dp[i-1][j-1] + 1
      else dp[i][j] = Math.max(dp[i-1][j], dp[i][j-1])
    }
  }
  var result = [], i = m, j = n
  while (i > 0 && j > 0) {
    if (a[i-1] === b[j-1]) { result.unshift({ type:'same', aIdx:i-1, bIdx:j-1 }); i--; j-- }
    else if (dp[i-1][j] >= dp[i][j-1]) { result.unshift({ type:'removed', aIdx:i-1, bIdx:-1 }); i-- }
    else { result.unshift({ type:'added', aIdx:-1, bIdx:j-1 }); j-- }
  }
  while (i > 0) { result.unshift({ type:'removed', aIdx:i-1, bIdx:-1 }); i-- }
  while (j > 0) { result.unshift({ type:'added', aIdx:-1, bIdx:j-1 }); j-- }
  return result
}

function charDiff(a, b) {
  if (!a || !b) return { aHtml: esc(a), bHtml: esc(b) }
  var seq = lcs(a.split(''), b.split(''))
  var aOut = '', bOut = ''
  var aIdx = 0, bIdx = 0
  for (var i = 0; i < seq.length; i++) {
    var s = seq[i]
    if (s.type === 'same') {
      aOut += esc(a.charAt(s.aIdx)); bOut += esc(b.charAt(s.bIdx))
    } else if (s.type === 'removed') {
      aOut += '<em class="cd cd-del">' + esc(a.charAt(s.aIdx)) + '</em>'
    } else {
      bOut += '<em class="cd cd-add">' + esc(b.charAt(s.bIdx)) + '</em>'
    }
  }
  return { aHtml: aOut, bHtml: bOut }
}

function esc(s) {
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
}

export default {
  name:'DiffViewer',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return {
      textA:'', textB:'', diffs:[], sameCount:0, diffCount:0,
      stats:{ same:0, added:0, removed:0, modified:0, diffCount:0 },
      markers:{ same:' ', removed:'—', added:'+', modified:'~' }
    }
  },
  computed:{
    hasContent:function(){ return this.textA.trim() || this.textB.trim() }
  },
  methods:{
    compare:function(){
      var linesA = this.textA.split('\n')
      var linesB = this.textB.split('\n')
      if (!this.textA.trim() && !this.textB.trim()) { this.diffs = []; this.stats = { same:0, added:0, removed:0, modified:0, diffCount:0 }; return }

      var seq = lcs(linesA, linesB)
      var diffs = []
      var same = 0, added = 0, removed = 0, modified = 0
      var i = 0
      while (i < seq.length) {
        var s = seq[i]
        if (s.type === 'same') {
          diffs.push({ type:'same', text:linesA[s.aIdx], lnA:s.aIdx+1, lnB:s.bIdx+1 })
          same++; i++
        } else if (s.type === 'removed') {
          // check if next is 'added' (modification)
          if (i + 1 < seq.length && seq[i+1].type === 'added') {
            var oldText = linesA[s.aIdx]
            var newText = linesB[seq[i+1].bIdx]
            diffs.push({ type:'modified', oldText:oldText, newText:newText, lnA:s.aIdx+1, lnB:seq[i+1].bIdx+1 })
            modified++; i += 2
          } else {
            diffs.push({ type:'removed', text:linesA[s.aIdx], lnA:s.aIdx+1, lnB:'' })
            removed++; i++
          }
        } else {
          diffs.push({ type:'added', text:linesB[s.bIdx], lnA:'', lnB:s.bIdx+1 })
          added++; i++
        }
      }

      this.diffs = diffs
      this.stats = { same:same, added:added, removed:removed, modified:modified, diffCount:added+removed+modified }
    },
    highlightChars:function(text, pairText){
      if (!pairText) return esc(text)
      var cd = charDiff(text, pairText)
      return cd.aHtml || cd.bHtml || esc(text)
    },
    highlightChange:function(oldText, newText){
      var cd = charDiff(oldText, newText)
      return '<span class="df-old-chunk">' + cd.aHtml + '</span><span class="df-new-chunk">' + cd.bHtml + '</span>'
    },
    copyDiff:function(){
      var text = this.diffs.map(function(d){
        if (d.type === 'same') return '  ' + d.text
        if (d.type === 'removed') return '- ' + d.text
        if (d.type === 'added') return '+ ' + d.text
        return '- ' + d.oldText + '\n+ ' + d.newText
      }).join('\n')
      navigator.clipboard.writeText(text); this.$message.success(this.$t('tool.DiffViewer.copiedDiff'))
    },
    doAction:function(a){
      if (a === 'clear') { this.textA = ''; this.textB = ''; this.diffs = []; this.stats = { same:0, added:0, removed:0, modified:0, diffCount:0 } }
    }
  }
}
</script>

<style scoped>
.df-tool { width: 100%; }

/* input */
.df-input-section { margin-bottom: 20px; }
.df-input-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #606266; margin-bottom: 12px; }
.df-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: #4e7cff; box-shadow: 0 0 6px rgba(78,124,255,.4); }
.df-panes { display: flex; gap: 16px; }
.df-pane { flex: 1; min-width: 0; }
.df-pane-title { font-size: 12px; font-weight: 600; color: #909399; margin-bottom: 6px; }
.df-textarea >>> textarea { font-family: 'Consolas','Courier New',monospace; font-size: 13px !important; line-height: 1.6 !important; }

/* stats */
.df-stats { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 16px; padding: 10px 16px; background: #f8f9fb; border-radius: 8px; }
.df-stat { font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 4px; }
.df-stat-green { color: #67c23a; background: #f0fff4; }
.df-stat-red { color: #f56c6c; background: #fff5f5; }
.df-stat-blue { color: #4e7cff; background: #f0f4ff; }
.df-stat-orange { color: #e6a23c; background: #fffdf5; }

/* output */
.df-output { background: #fff; border: 1px solid #ebeef5; border-radius: 10px; overflow: hidden; }
.df-lines { font-family: 'Consolas','Courier New',monospace; font-size: 13px; line-height: 1.7; }
.df-line { display: flex; align-items: baseline; padding: 2px 12px; min-height: 28px; }
.df-line:hover { filter: brightness(0.97); }

.df-line-same { background: #fff; }
.df-line-added { background: #e6ffec; }
.df-line-removed { background: #ffeef0; }
.df-line-modified { background: #fff8e8; }

.df-ln { width: 36px; flex-shrink: 0; text-align: right; padding-right: 8px; font-size: 11px; color: #c0c4cc; user-select: none; }
.df-marker { width: 20px; flex-shrink: 0; text-align: center; font-size: 11px; font-weight: 700; user-select: none; }
.df-line-added .df-marker { color: #67c23a; }
.df-line-removed .df-marker { color: #f56c6c; }
.df-line-modified .df-marker { color: #e6a23c; }
.df-line-same .df-marker { color: #c0c4cc; }

.df-text { white-space: pre-wrap; word-break: break-all; flex: 1; color: #303133; }
.df-line-removed .df-text { color: #c62828; }
.df-line-added .df-text { color: #2e7d32; }

/* char diff highlights */
.df-text >>> .cd { font-style: normal; padding: 1px 2px; border-radius: 2px; }
.df-text >>> .cd-del { background: #ffcdd2; color: #c62828; text-decoration: line-through; }
.df-text >>> .cd-add { background: #a5d6a7; color: #1b5e20; }
.df-old-chunk { color: #c62828; }
.df-new-chunk { color: #2e7d32; padding-left: 12px; border-left: 2px solid #e0e0e0; margin-left: 4px; }
.df-old-chunk >>> .cd-del { background: #ffcdd2; }
.df-new-chunk >>> .cd-add { background: #a5d6a7; }

.df-empty { text-align: center; padding: 60px 20px; color: #c0c4cc; font-size: 14px; }

@media (max-width: 640px) {
  .df-panes { flex-direction: column; }
}
</style>
